import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Receipt, CheckCircle, XCircle, Filter, Search, Eye, Banknote } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fetchAllReimbursements, updateReimbursementStatus, markReimbursementPaid } from "@/services/reimbursementService";
import ReimbursementDetailsDialog from "@/components/ReimbursementDetailsDialog";
import { isAuthExpiryError } from "@/utils/errorHandler";
import { usePermission } from "@/contexts/RoleAccessContext";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
  PaginationPageSize,
} from "@/components/ui/pagination";

const ReimbursementManagement = () => {
  const [claims, setClaims] = useState([]);
  const [filteredClaims, setFilteredClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [markPaidDialogOpen, setMarkPaidDialogOpen] = useState(false);
  const [adminComment, setAdminComment] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const { can } = usePermission();
  // Approve / reject / mark paid all need the edit permission.
  const canEditClaims = can("reimbursement_management", "edit");

  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    setPage(1);
  };

  useEffect(() => {
    fetchClaims();
  }, []);

  useEffect(() => {
    filterClaims();
  }, [claims, searchTerm, statusFilter]);

  // ----- Pagination (5 per page) -----
  const totalPages = Math.max(1, Math.ceil(filteredClaims.length / pageSize));

  const pagedClaims = React.useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredClaims.slice(start, start + pageSize);
  }, [filteredClaims, page, pageSize]);

  // Reset to page 1 when the filters change; clamp if the list shrank below
  // the current page.
  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const buildPages = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages = new Set([1, total, current, current - 1, current + 1]);
    const cleaned = [...pages]
      .filter((p) => p >= 1 && p <= total)
      .sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < cleaned.length; i++) {
      result.push(cleaned[i]);
      if (i < cleaned.length - 1 && cleaned[i + 1] - cleaned[i] > 1) {
        result.push("...");
      }
    }
    return result;
  };

  const fetchClaims = async () => {
    try {
      const res = await fetchAllReimbursements();
      setClaims(res?.data?.reimbursements || []);
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        console.error("Error fetching reimbursement claims:", error);
        toast.error("Failed to load reimbursement claims");
      }
    } finally {
      setLoading(false);
    }
  };

  const filterClaims = () => {
    let filtered = claims;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          (c.employeeName || "").toLowerCase().includes(term) ||
          (c.employeeCode || "").toLowerCase().includes(term) ||
          (c.claim_type || "").toLowerCase().includes(term),
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((c) => c.status === statusFilter);
    }

    setFilteredClaims(filtered);
  };

  const formatCurrency = (amount) => `₹${Number(amount || 0).toLocaleString()}`;

  const formatDate = (date) => {
    if (!date) return "—";
    return format(new Date(date), "dd MMM yyyy");
  };

  const formatDateTime = (date) => {
    if (!date) return "—";
    return format(new Date(date), "dd MMM yyyy, h:mm a");
  };

  // "June 2026" from a { month, year } pay period (month is 1-12).
  const formatPayPeriod = (pp) => {
    if (!pp || pp.month == null || pp.year == null) return "";
    return format(new Date(pp.year, pp.month - 1, 1), "MMMM yyyy");
  };

  const resetSelection = () => {
    setSelectedClaim(null);
    setAdminComment("");
    setRejectionReason("");
  };

  const handleApprove = async () => {
    if (!selectedClaim) return;
    setActionLoading(true);
    try {
      await updateReimbursementStatus(selectedClaim._id, {
        status: "approved",
        admin_comment: adminComment || undefined,
      });
      toast.success("Reimbursement approved");
      setApproveDialogOpen(false);
      resetSelection();
      fetchClaims();
    } catch (error) {
      toast.error("Failed to approve claim");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedClaim) return;
    setActionLoading(true);
    try {
      await updateReimbursementStatus(selectedClaim._id, {
        status: "rejected",
        rejection_reason: rejectionReason || undefined,
      });
      toast.success("Reimbursement rejected");
      setRejectDialogOpen(false);
      resetSelection();
      fetchClaims();
    } catch (error) {
      toast.error("Failed to reject claim");
    } finally {
      setActionLoading(false);
    }
  };

  // Record an off-payroll cash payment: approved → paid (paid_manually = true).
  const handleMarkPaid = async () => {
    if (!selectedClaim) return;
    setActionLoading(true);
    try {
      await markReimbursementPaid(selectedClaim._id);
      toast.success("Reimbursement marked as paid");
      setMarkPaidDialogOpen(false);
      resetSelection();
      fetchClaims();
    } catch (error) {
      toast.error("Failed to mark as paid");
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      submitted: "secondary",
      approved: "default",
      paid: "default",
      rejected: "destructive",
      cancelled: "outline",
    };
    return (
      <Badge variant={variants[status] || "outline"} className="capitalize">
        {status}
      </Badge>
    );
  };

  const stats = {
    total: claims.length,
    pending: claims.filter((c) => c.status === "submitted").length,
    approved: claims.filter((c) => c.status === "approved").length,
    paid: claims.filter((c) => c.status === "paid").length,
    rejected: claims.filter(
      (c) => c.status === "rejected" || c.status === "cancelled",
    ).length,
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-80" />
        </div>
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
          {Array.from({ length: 5 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-5 space-y-3">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="p-6 space-y-3">
            <Skeleton className="h-10 w-full" />
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Reimbursement Management
        </h1>
        <p className="text-gray-500">
          Review and approve employee reimbursement claims
        </p>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground">Total</p>
            <p className="text-2xl font-bold text-primary">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Pending
            </p>
            <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-500">
              {stats.pending}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Approved
            </p>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-500">
              {stats.approved}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Paid
            </p>
            <p className="text-2xl font-bold text-green-600 dark:text-green-500">
              {stats.paid}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Rejected / Cancelled
            </p>
            <p className="text-2xl font-bold text-red-600 dark:text-red-500">
              {stats.rejected}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Claims Table */}
      <Card>
        <CardHeader>
          <CardTitle>Reimbursement Claims</CardTitle>
        </CardHeader>
        <div className="flex flex-col sm:flex-row gap-4 mb-5 px-6 items-center justify-between">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by employee name, ID, or type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="submitted">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <CardContent>
          <div
            className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg thin-scrollbar"
            style={{ scrollbarWidth: "thin" }}
          >
            <Table>
              <TableHeader>
                <TableRow className="border-b border-gray-200 dark:border-gray-700">
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Employee
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Type
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Amount
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Expense Date
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    Submitted At
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Status
                  </TableHead>
                  <TableHead className="font-semibold text-center w-[200px]">
                    Action
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedClaims.map((claim) => (
                  <TableRow
                    key={claim._id}
                    className="border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-muted/50"
                    onClick={() => {
                      setSelectedClaim(claim);
                      setDetailsOpen(true);
                    }}
                  >
                    <TableCell className="font-medium">
                      <p className="font-medium text-gray-900 dark:text-gray-200">
                        {claim.employeeName || "—"}
                      </p>
                      <p className="text-sm text-muted-foreground dark:text-gray-400">
                        {claim.employeeCode || ""}
                      </p>
                    </TableCell>
                    <TableCell className="text-gray-800 dark:text-gray-300">
                      {claim.claim_type}
                    </TableCell>
                    <TableCell className="text-gray-800 dark:text-gray-300">
                      {formatCurrency(claim.amount)}
                    </TableCell>
                    <TableCell className="text-gray-800 dark:text-gray-300">
                      {formatDate(claim.expense_date)}
                    </TableCell>
                    <TableCell className="text-gray-800 dark:text-gray-300 whitespace-nowrap">
                      {formatDate(claim.created_at || claim.applied_date)}
                    </TableCell>
                    <TableCell>{getStatusBadge(claim.status)}</TableCell>
                    <TableCell
                      className="pr-6 py-4"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-xs font-semibold"
                          onClick={() => {
                            setSelectedClaim(claim);
                            setDetailsOpen(true);
                          }}
                        >
                          <Eye className="w-3.5 h-3.5 opacity-60" />
                        </Button>

                        {canEditClaims && claim.status === "submitted" && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-xs font-semibold text-emerald-600 hover:bg-emerald-50"
                              onClick={() => {
                                setSelectedClaim(claim);
                                setAdminComment("");
                                setApproveDialogOpen(true);
                              }}
                            >
                              <CheckCircle className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                              onClick={() => {
                                setSelectedClaim(claim);
                                setRejectionReason("");
                                setRejectDialogOpen(true);
                              }}
                            >
                              <XCircle className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}

                        {canEditClaims && claim.status === "approved" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-xs font-semibold text-emerald-600 hover:bg-emerald-50"
                            title="Mark as paid (manual)"
                            onClick={() => {
                              setSelectedClaim(claim);
                              setMarkPaidDialogOpen(true);
                            }}
                          >
                            <Banknote className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {filteredClaims.length === 0 && (
            <div className="text-center py-8">
              <Receipt className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                No reimbursement claims found
              </h3>
              <p className="text-gray-500">
                {searchTerm || statusFilter !== "all"
                  ? "Try adjusting your search or filter criteria"
                  : "Reimbursement claims from employees will appear here"}
              </p>
            </div>
          )}

          {filteredClaims.length > 0 && (
            <div className="flex items-center justify-end sm:justify-between gap-3 mt-4">
              <PaginationPageSize
                value={pageSize}
                onValueChange={handlePageSizeChange}
              />
              <Pagination className="mx-0 w-auto justify-end">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      aria-disabled={page === 1}
                      className={
                        page === 1 ? "pointer-events-none opacity-50" : ""
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        setPage((p) => Math.max(1, p - 1));
                      }}
                    />
                  </PaginationItem>

                  {buildPages(page, totalPages).map((p, idx) => {
                    if (p === "...") {
                      return (
                        <PaginationItem key={`e-${idx}`}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      );
                    }
                    return (
                      <PaginationItem key={p}>
                        <PaginationLink
                          href="#"
                          isActive={p === page}
                          onClick={(e) => {
                            e.preventDefault();
                            setPage(p);
                          }}
                        >
                          {p}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}

                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      aria-disabled={page === totalPages}
                      className={
                        page === totalPages
                          ? "pointer-events-none opacity-50"
                          : ""
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        setPage((p) => Math.min(totalPages, p + 1));
                      }}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Reimbursement</DialogTitle>
          </DialogHeader>
          {selectedClaim && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Employee</p>
                  <p className="font-medium">{selectedClaim.employeeName}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Claimed Amount</p>
                  <p className="font-medium">
                    {formatCurrency(selectedClaim.amount)}
                  </p>
                </div>
              </div>
              <div>
                <Label>Comment (optional)</Label>
                <Textarea
                  value={adminComment}
                  onChange={(e) => setAdminComment(e.target.value)}
                  rows={3}
                />
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setApproveDialogOpen(false);
                    resetSelection();
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={handleApprove} disabled={actionLoading}>
                  {actionLoading ? "Approving..." : "Approve"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Reimbursement</DialogTitle>
          </DialogHeader>
          {selectedClaim && (
            <div className="space-y-4">
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Reason for rejection"
                rows={4}
              />
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setRejectDialogOpen(false);
                    resetSelection();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  disabled={actionLoading}
                  onClick={handleReject}
                >
                  {actionLoading ? "Rejecting..." : "Reject"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mark as Paid (manual) Dialog */}
      <Dialog open={markPaidDialogOpen} onOpenChange={setMarkPaidDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Reimbursement as Paid</DialogTitle>
          </DialogHeader>
          {selectedClaim && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Record that the company paid this reimbursement manually
                (outside payroll). This marks the claim as paid and can't be
                undone.
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Employee</p>
                  <p className="font-medium">{selectedClaim.employeeName}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Approved Amount</p>
                  <p className="font-medium">
                    {formatCurrency(
                      selectedClaim.approved_amount ?? selectedClaim.amount,
                    )}
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setMarkPaidDialogOpen(false);
                    resetSelection();
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={handleMarkPaid} disabled={actionLoading}>
                  {actionLoading ? "Marking..." : "Mark as Paid"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <ReimbursementDetailsDialog
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        claim={selectedClaim}
      />
    </div>
  );
};

export default ReimbursementManagement;
